import { Grid } from "@mui/material";
import Box from "@mui/material/Box";
import { useEffect, useState, VFC } from "react";
import { useTheme, useMediaQuery } from "@mui/material";
import ButtonArea from "../common/ButtonArea";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { DeviceDetailRequest, DeviceDetailResponse } from "./api/TerminalForm";
import { TerminalService } from "./api/TerminalService";
import { AxiosResponse } from "axios";
import Constants from "../../utils/Constants";
import { ResultDialog, ResultResponse } from "../common/dialog/ResultDialog";
import DeviceDetailView from "../common/detailview/DeviceDetailView";
import { endOfMonth, startOfMonth } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { DeviceUiConverter } from "../../converter/DeviceUiConverter";
import { get } from "http";

const TerminalDetail: VFC = () => {
  const PAGE_NAME = "端末詳細";
  const navigate = useNavigate();
  const now = new Date();

  /***************************************************************
   * State管理
   **************************************************************/
  const searchForm = useForm<DeviceDetailRequest>({
    reValidateMode: "onSubmit",
    defaultValues: {
      id: sessionStorage.getItem("DEVICE_ID") ?? "",
      simReplacementLogDateStart: startOfMonth(now).toISOString(),
      simReplacementLogDateEnd: endOfMonth(now).toISOString(),
    },
  });
  const {
    handleSubmit,
    getValues,
  } = searchForm;

  // 端末情報
  const [device, setDevice] = useState(new DeviceDetailResponse());

  // 編集ボタン(活性・非活性)
  const [isDisable, setIsDisable] = useState(false);

  // 共通ダイアログ(ステータス)
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // 処理結果
  const [resultResponse, setResultResponse] = useState<ResultResponse>({
    status: "",
    code: "",
    displayMessage: "",
    result: [],
    errorMessages: [],
  });

  /***************************************************************
   * 共通処理
   **************************************************************/
  // 処理結果ダイアログ(CLOSE)
  const handleResultDialogClose = () => {
    setIsDialogOpen(false);
  };
  // 処理結果を設定
  const setStateResultResponse = (
    status: string,
    code: string,
    displayMessage: string,
    result: string
  ) => {
    resultResponse.status = status;
    resultResponse.code = code;
    resultResponse.displayMessage = displayMessage;
    resultResponse.result = [result];

    setIsDialogOpen(true);
    setIsDisable(true);
    setResultResponse(resultResponse);
  };

  /***************************************************************
   * 初期表示処理
   **************************************************************/
  useEffect(() => {
    // 端末情報取得
    findById(getValues());
  }, []);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  // xs: 0.875rem, lg以上: 1.25rem, それ以外は1rem
  const contentFontSize = isXs ? '0.875rem' : isLgUp ? '1.25rem' : '1rem';

  // 端末詳細取得API
  const findById = (request: DeviceDetailRequest) => {
    const service = new TerminalService();
    // SIM差し替えログ日時検索の終了日を設定
    if (request.simReplacementLogDateStart !== "") {
      request.simReplacementLogDateEnd = endOfMonth(new Date(request.simReplacementLogDateStart)).toISOString();
    }
    if ("" !== request.id) {
      service
        .findDetailByParams(request)
        .then((response: AxiosResponse) => {
          if (Constants.ERR_STATUS_OK === response.data.status) {
            setDevice(response.data.result);
          } else {
            setIsDialogOpen(true);
            setIsDisable(true);
            setResultResponse(response.data);
          }
        })
        .catch((err) => {
          setStateResultResponse(
            Constants.ERR_STATUS_NG,
            err.response.status,
            err.response.statusText,
            Constants.E_MSG_SYSTEM_ERR
          );
        })
        .finally(() => {
          // TODO 必要ならローディング
        });
    } else {
      setStateResultResponse(
        Constants.ERR_STATUS_NG,
        Constants.ERR_CODE_404,
        Constants.T_MSG_NO_DATA,
        Constants.W_MSG_NO_DATA
      );
    }
  };

  return (
    <>
      <Box
        display={"flex"}
        flexDirection={"column"}
        minWidth="600px"
        sx={{
          height: "calc(100vh - 64px)",
          width: { xs: "100vw", sm: "calc(100vw - 200px)" },
          overflowX: "auto"
        }}
      >
        <Box sx={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: '#fff' }}>
          <ButtonArea
            pageName={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: { xs: 'wrap', lg: 'nowrap' }, marginTop: { xs: '-2px', lg: '2.5px' } }}>
                <span>{PAGE_NAME}</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: { xs: '0.875rem', lg: '1.2rem' }, flexBasis: { xs: '100%', lg: 'auto' }, marginTop: { xs: '0', lg: '-2.5px' } }}>
                  <Box sx={{marginTop: { xs: '-2px', lg: '0px' }}} component="span">
                    {device.modelName === "FS010M" || device.modelName === "m2u300"
                      ? `シリアルナンバー：${device.serialNumber}`
                      : `IMEI：${device.imei}`}
                  </Box>
                  {device.modelName && device.modelName.trim() !== '' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '0.875rem', lg: '1.2rem' }, gap: 2, marginTop: { xs: '-6px', lg: '0px' } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          border: '1px solid #bbb',
                          backgroundColor: '#e7f2ff',
                          borderRadius: '4px',
                          paddingX: { xs: '4px', lg: '8px' },
                          paddingTop: { xs: '2px', lg: '0px' },
                          height: { xs: '22px', lg: '24px' },
                          width: 'fit-content',
                        }}
                      >
                        <Box sx={{marginTop: { xs: '-1.5px', lg: '0px' }}}>
                          {DeviceUiConverter.getNetworkStatusIcon(
                            device.isOnline,
                            contentFontSize
                          )}
                        </Box>
                        <Box
                          style={{
                            marginLeft: '0.5em',
                            fontSize: undefined,
                            verticalAlign: 'middle',
                            whiteSpace: 'nowrap',
                          }}
                          // @ts-ignore　//この次の行の型チェックエラーを無視してほしい」と指示するコメント
                          sx={{ paddingTop: { xs: '2px', lg: '0px' } }}
                        >
                          {device.isOnline === 'オンライン' ? 'オンライン' : 'オフライン'}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            }
            isBackBtn={true}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/terminal_edit")}
              disabled={isDisable}
              sx={{ px: { xs: 1, lg: 2 } }}
            >
              {/* 端末編集 */}
              端末<Box component="br" sx={{ display: { xs: 'block', lg: 'none' } }} />編集
            </Button>
          </ButtonArea>
        </Box>

        <form onSubmit={handleSubmit(findById)}>
          <FormProvider {...searchForm}>
            <Box>
              <Grid container direction="column" spacing={2}>
                <DeviceDetailView device={device} />
              </Grid>
            </Box>
          </FormProvider>
        </form>
      </Box>

      {/* 処理結果ダイアログ */}
      <ResultDialog
        isDialogOpen={isDialogOpen}
        resultResponse={resultResponse}
        handler={handleResultDialogClose}
      />
    </>
  );
};

export default TerminalDetail;
